<template>
  <section class="briefing">
    <div>
      <h1>{{ challengesTitle }}</h1>
    </div>
    <div v-if="showAttackMap" class="briefing-meta">
      <span>{{ availableTargets }} available</span>
      <span>{{ solvedTargets }} solved</span>
    </div>
  </section>

  <section v-if="showAttackMap" class="operations">
    <aside class="legend-panel">
      <h2>Operations</h2>
      <div class="legend-list">
        <span><i class="swatch live" /> Active target</span>
        <span><i class="swatch solved" /> Cleared target</span>
      </div>
      <div class="sector-list">
        <button
          v-for="sector in primaryFilters"
          :key="sector.name"
          class="sector-row"
          :class="{ active: selectedCategory === sector.name }"
          :style="sectorStyle(sector)"
          type="button"
          @click="toggleCategory(sector.name)"
        >
          <strong>{{ sector.name }}</strong>
          <span>{{ sector.solved }}/{{ sector.count }}</span>
        </button>
      </div>
      <div v-if="specialFilters.length" class="sector-list sector-list-special">
        <button
          v-for="sector in specialFilters"
          :key="sector.name"
          class="sector-row"
          :class="{ active: selectedCategory === sector.name }"
          :style="sectorStyle(sector)"
          type="button"
          @click="toggleCategory(sector.name)"
        >
          <strong>{{ sector.name }}</strong>
          <span>{{ sector.solved }}/{{ sector.count }}</span>
        </button>
      </div>
      <div class="zoom-controls">
        <button class="button" @click="zoomOut">
          -
        </button>
        <span>{{ Math.round(zoom * 100) }}%</span>
        <button class="button" @click="zoomIn">
          +
        </button>
        <button class="button" @click="resetZoom">
          Reset
        </button>
      </div>
    </aside>

    <div
      ref="viewport"
      class="map-viewport"
      @mousedown="dragStart"
      @mousemove="dragMove"
      @mouseup="dragEnd"
      @mouseleave="dragEnd"
      @scroll="clampScroll"
      @wheel.prevent="zoomWheel"
    >
      <div class="map-stage" :style="stageStyle">
        <div
          class="map-surface"
          :style="surfaceStyle"
          :data-sweden-polygon="polygonData"
        >
          <div class="watermark north">
            NORR
          </div>
          <div class="watermark baltic">
            Baltic Sea
          </div>

          <svg
            class="map-layer"
            :viewBox="`0 0 ${mapWidth} ${mapHeight}`"
            preserveAspectRatio="none"
          >
            <polygon
              v-for="(polygon, index) in polygonPaths"
              :key="`sweden-${index}`"
              class="sweden-outline"
              :points="polygon"
            />
            <line
              v-for="route in routes"
              :key="`${route.from}-${route.to}`"
              :x1="route.x1"
              :y1="route.y1"
              :x2="route.x2"
              :y2="route.y2"
              class="route-line"
              :class="route.state"
            />
          </svg>

          <button
            v-for="target in mapTargets"
            :key="target.id"
            class="target-marker"
            :class="markerClass(target)"
            :style="markerStyle(target)"
            :title="tooltip(target)"
            @click.prevent
            @mouseup.stop.prevent="releaseChallenge(target, $event)"
          >
            <span class="target-pin" />
            <span class="target-card" :style="cardStyle(target)">
              <span class="target-tags">
                <span class="target-tag target-tag-primary">{{ target.categories[0] }}</span>
                <span
                  v-for="category in target.categories.slice(1)"
                  :key="`${target.id}-${category}`"
                  class="target-tag target-tag-secondary"
                >{{ category }}</span>
              </span>
              <strong class="target-title">{{ target.status === 2 ? 'Classified' : target.title }}</strong>
              <span class="target-stats">
                <span class="target-stat">
                  <span class="target-stat-icon material-icons">military_tech</span>
                  {{ $store.getters.challScore(target.id) }}
                </span>
                <span class="target-stat">
                  <span class="target-stat-icon material-icons">flag</span>
                  {{ target.solves }}
                </span>
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </section>

  <p v-else-if="emptyChallengesMessage" class="empty-copy">
    {{ emptyChallengesMessage }}
  </p>
</template>

<script>
const MAP_WIDTH = 1520
const MAP_HEIGHT = 1840
const SEA_MARGIN_X = 220
const SEA_MARGIN_Y = 110
// Traced from Sweden country geometry derived from Natural Earth admin-0 country data.
const BASE_SWEDEN_POLYGONS = [
    [[682, 429], [632, 503], [640, 568], [558, 654], [459, 745], [422, 895], [459, 970], [508, 1029], [461, 1148], [407, 1173], [388, 1352], [359, 1451], [296, 1441], [267, 1525], [208, 1530], [192, 1430], [149, 1309], [110, 1159], [133, 1098], [175, 1025], [192, 900], [159, 846], [156, 705], [190, 605], [240, 607], [258, 565], [240, 529], [319, 379], [370, 262], [404, 186], [453, 186], [467, 127], [564, 144], [571, 74], [603, 70], [671, 122], [751, 194], [752, 358], [770, 399], [682, 429]], [[419, 1315], [427, 1321], [387, 1443], [383, 1403], [419, 1315]], [[537, 1254], [508, 1287], [510, 1309], [518, 1309], [516, 1317], [503, 1323], [504, 1334], [491, 1342], [484, 1364], [473, 1368], [477, 1344], [471, 1328], [475, 1314], [472, 1298], [501, 1257], [521, 1256], [524, 1250], [538, 1250], [537, 1254]], [[613, 631], [624, 630], [620, 643], [612, 657], [605, 656], [612, 643], [611, 636], [613, 631]],
]
const SWEDEN_POLYGONS = BASE_SWEDEN_POLYGONS.map((polygon) => polygon.map(([x, y]) => [x + SEA_MARGIN_X, y + SEA_MARGIN_Y]))
const SWEDEN_MAINLAND_POLYGON = SWEDEN_POLYGONS[0]

function getPolygonBounds (polygon) {
    return polygon.reduce((bounds, [x, y]) => ({
        minX: Math.min(bounds.minX, x),
        maxX: Math.max(bounds.maxX, x),
        minY: Math.min(bounds.minY, y),
        maxY: Math.max(bounds.maxY, y),
    }), {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
    })
}

const MAINLAND_BOUNDS = getPolygonBounds(SWEDEN_MAINLAND_POLYGON)
const MAINLAND_CENTER_X = (MAINLAND_BOUNDS.minX + MAINLAND_BOUNDS.maxX) / 2
const PLACEMENT_BOUNDS = {
    minX: MAINLAND_BOUNDS.minX + 22,
    maxX: MAINLAND_BOUNDS.maxX - 22,
    minY: MAINLAND_BOUNDS.minY + 18,
    maxY: MAINLAND_BOUNDS.maxY - 18,
}
const CARD_SIZE = {
    width: 92,
    height: 30,
}
const CARD_VERTICAL_OFFSET = 4
const CATEGORY_FOCUS_PADDING_X = 110
const CATEGORY_FOCUS_PADDING_Y = 120
const SPECIAL_FILTERS = ['onsite', 'easy', 'medium', 'hard', 'extreme']
const DRAG_OPEN_THRESHOLD = 8
const DIFFICULTY_PULSE_COLORS = {
    easy: 'rgba(34, 197, 94, 0.78)',
    medium: 'rgba(234, 179, 8, 0.78)',
    hard: 'rgba(239, 68, 68, 0.78)',
    extreme: 'rgba(0, 0, 0, 0.82)',
}
const CARD_RADII = [24]
const CARD_ANGLES = [
    Math.PI * 0.18, Math.PI * 0.28, Math.PI * 0.38, Math.PI * 0.62, Math.PI * 0.72, Math.PI * 0.82,
]

function polygonToString (polygon) {
    return polygon.map(point => point.join(',')).join(' ')
}

function pointInPolygon (point, polygon) {
    const [x, y] = point
    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0]
        const yi = polygon[i][1]
        const xj = polygon[j][0]
        const yj = polygon[j][1]
        const intersect = ((yi > y) !== (yj > y)) &&
            (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-9) + xi)
        if (intersect) inside = !inside
    }
    return inside
}

function hashSeed (value) {
    let hash = 2166136261
    const text = String(value)
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i)
        hash = Math.imul(hash, 16777619)
    }
    return hash >>> 0
}

function mulberry32 (seed) {
    return function () {
        let t = seed += 0x6D2B79F5
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

function createPlacements (count) {
    const placements = []

    for (let index = 0; index < count; index++) {
        const rng = mulberry32(hashSeed(`placement-${index}`))
        let best = null
        let minDistance = 92

        for (let attempt = 0; attempt < 2400; attempt++) {
            if (attempt === 900) minDistance = 82
            if (attempt === 1500) minDistance = 70

            const candidate = {
                x: Math.round(PLACEMENT_BOUNDS.minX + rng() * (PLACEMENT_BOUNDS.maxX - PLACEMENT_BOUNDS.minX)),
                y: Math.round(PLACEMENT_BOUNDS.minY + rng() * (PLACEMENT_BOUNDS.maxY - PLACEMENT_BOUNDS.minY)),
            }

            if (!pointInPolygon([candidate.x, candidate.y], SWEDEN_MAINLAND_POLYGON)) continue

            const nearest = placements.reduce((distance, placed) => {
                const dx = candidate.x - placed.x
                const dy = candidate.y - placed.y
                return Math.min(distance, Math.sqrt(dx * dx + dy * dy))
            }, Infinity)

            if (nearest >= minDistance) {
                best = candidate
                break
            }

            if (nearest > (best?.nearest || 0)) {
                best = { ...candidate, nearest }
            }
        }

        const finalPoint = best || { x: 520, y: 820, nearest: 0 }
        placements.push({
            x: finalPoint.x,
            y: finalPoint.y,
            side: finalPoint.x > MAINLAND_CENTER_X ? 'right' : 'left',
        })
    }

    return placements.sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y
        return a.x - b.x
    })
}

function rectsOverlap (a, b) {
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom)
}

function pointToRectDistance (point, rect) {
    const dx = Math.max(rect.left - point.x, 0, point.x - rect.right)
    const dy = Math.max(rect.top - point.y, 0, point.y - rect.bottom)
    return Math.sqrt(dx * dx + dy * dy)
}

function connectorPoint (rect) {
    return {
        x: rect.left + CARD_SIZE.width / 2,
        y: rect.top + CARD_SIZE.height / 2,
    }
}

function placeCards (targets) {
    const placedRects = []
    const anchors = targets.map(target => ({ id: target.id, x: target.x, y: target.y }))

    return [...targets].sort((a, b) => a.y - b.y).map(target => {
        let bestPlacement = null
        let bestScore = Infinity

        CARD_RADII.forEach((radius, radiusIndex) => {
            CARD_ANGLES.forEach((angle, angleIndex) => {
                const cardCenterX = target.x + Math.cos(angle) * radius
                const cardCenterY = target.y + Math.sin(angle) * radius
                const rect = {
                    left: cardCenterX - CARD_SIZE.width / 2,
                    top: cardCenterY - CARD_SIZE.height / 2,
                    right: cardCenterX + CARD_SIZE.width / 2,
                    bottom: cardCenterY + CARD_SIZE.height / 2,
                }

                let score = 0

                if (rect.left < 18) score += (18 - rect.left) * 12
                if (rect.right > MAP_WIDTH - 18) score += (rect.right - (MAP_WIDTH - 18)) * 12
                if (rect.top < 18) score += (18 - rect.top) * 12
                if (rect.bottom > MAP_HEIGHT - 18) score += (rect.bottom - (MAP_HEIGHT - 18)) * 12

                anchors.forEach((anchor) => {
                    const distance = pointToRectDistance(anchor, rect)
                    if (distance < 26) {
                        score += 14000 + (26 - distance) * 360
                    }
                })

                placedRects.forEach((placed) => {
                    if (rectsOverlap(rect, placed)) {
                        const overlapX = Math.min(rect.right, placed.right) - Math.max(rect.left, placed.left)
                        const overlapY = Math.min(rect.bottom, placed.bottom) - Math.max(rect.top, placed.top)
                        score += 18000 + Math.max(0, overlapX) * Math.max(0, overlapY) * 8
                    } else {
                        const gapX = Math.max(0, Math.max(placed.left - rect.right, rect.left - placed.right))
                        const gapY = Math.max(0, Math.max(placed.top - rect.bottom, rect.top - placed.bottom))
                        const gap = Math.sqrt(gapX * gapX + gapY * gapY)
                        if (gap < 32) score += (32 - gap) * 40
                    }
                })

                score += radiusIndex * 8
                score += angleIndex * 0.2

                if (score < bestScore) {
                    bestScore = score
                    bestPlacement = {
                        rect,
                        connector: connectorPoint(rect),
                    }
                }
            })
        })

        placedRects.push(bestPlacement.rect)

        return {
            ...target,
            cardLeft: bestPlacement.rect.left,
            cardTop: bestPlacement.rect.top + CARD_VERTICAL_OFFSET,
            connectorX: bestPlacement.connector.x,
            connectorY: bestPlacement.connector.y + CARD_VERTICAL_OFFSET,
        }
    })
}

export default {
    data () {
        return {
            zoom: 1,
            dragState: null,
            selectedCategory: null,
            viewportWidth: 0,
            viewportHeight: 0,
            cameraAnimationFrame: null,
        }
    },
    computed: {
        now () {
            return new Date()
        },
        ctfHasStarted () {
            const startTime = this.$store.state.start_time
            return startTime instanceof Date && !Number.isNaN(startTime.getTime()) && this.now >= startTime
        },
        hasChallenges () {
            return this.challengeList.length > 0
        },
        showAttackMap () {
            return this.ctfHasStarted && this.hasChallenges
        },
        challengesTitle () {
            return this.showAttackMap ? 'Attack Map' : 'Challenges'
        },
        emptyChallengesMessage () {
            if (!this.$store.state.watsupReturned) return ''
            if (!this.ctfHasStarted) return 'Challenges will appear when the CTF starts.'
            if (!this.hasChallenges) return 'No challenges are available right now.'
            return ''
        },
        polygonData () {
            return polygonToString(SWEDEN_MAINLAND_POLYGON)
        },
        mapWidth () {
            return MAP_WIDTH
        },
        mapHeight () {
            return MAP_HEIGHT
        },
        polygonPaths () {
            return SWEDEN_POLYGONS.map(polygon => polygonToString(polygon))
        },
        challengeList () {
            return Object.values(this.$store.state.challenges).sort((a, b) => a.order - b.order)
        },
        primaryFilters () {
            return this.$store.state.categories.map((name, index) => {
                const challenges = this.challengeList.filter(challenge => this.primaryCategory(challenge) === name)
                return {
                    name,
                    index,
                    type: 'primary',
                    count: challenges.length,
                    live: challenges.filter(challenge => challenge.status === 0).length,
                    solved: challenges.filter(challenge => this.isSolved(challenge)).length,
                }
            })
        },
        specialFilters () {
            return SPECIAL_FILTERS.map((name, index) => {
                const challenges = this.challengeList.filter(challenge => this.matchesFilter(challenge, name))
                return {
                    name,
                    index: this.$store.state.categories.length + index,
                    type: 'special',
                    count: challenges.length,
                    live: challenges.filter(challenge => challenge.status === 0).length,
                    solved: challenges.filter(challenge => this.isSolved(challenge)).length,
                }
            }).filter(filter => filter.count > 0)
        },
        filteredChallengeList () {
            if (!this.selectedCategory) return this.challengeList
            return this.challengeList.filter(challenge => this.matchesFilter(challenge, this.selectedCategory))
        },
        allMapTargets () {
            const groupedChallenges = this.$store.state.categories.flatMap((category) => {
                return this.challengeList
                    .filter(challenge => this.primaryCategory(challenge) === category)
                    .sort((a, b) => a.order - b.order)
            })

            const placements = createPlacements(groupedChallenges.length)
            const anchoredTargets = groupedChallenges.map((challenge, index) => ({
                ...challenge,
                ...placements[index],
                sectorIndex: this.$store.state.categories.indexOf(this.primaryCategory(challenge)),
            }))

            return placeCards(anchoredTargets)
        },
        mapTargets () {
            if (!this.selectedCategory) return this.allMapTargets
            const visibleIds = new Set(this.filteredChallengeList.map(challenge => challenge.id))
            return this.allMapTargets.filter(target => visibleIds.has(target.id))
        },
        routes () {
            const positions = this.mapTargets.reduce((acc, target) => {
                acc[target.id] = target
                return acc
            }, {})

            return this.mapTargets
                .filter(target => target.unlocked_by !== null && positions[target.unlocked_by])
                .map(target => {
                    const source = positions[target.unlocked_by]
                    return {
                        from: source.id,
                        to: target.id,
                        x1: source.x,
                        y1: source.y,
                        x2: target.x,
                        y2: target.y,
                        state: this.routeState(source, target),
                    }
                })
        },
        availableTargets () {
            return this.challengeList.filter(challenge => challenge.status === 0).length
        },
        solvedTargets () {
            return this.challengeList.filter(challenge => this.isSolved(challenge)).length
        },
        surfaceStyle () {
            const { offsetX, offsetY } = this.mapFrame
            return {
                transform: `translate(${offsetX}px, ${offsetY}px) scale(${this.zoom})`,
            }
        },
        stageStyle () {
            return {
                width: `${this.mapFrame.stageWidth}px`,
                height: `${this.mapFrame.stageHeight}px`,
                '--grid-size': `${60 * this.zoom}px`,
            }
        },
        mapFrame () {
            const scaledWidth = MAP_WIDTH * this.zoom
            const scaledHeight = MAP_HEIGHT * this.zoom
            const stageWidth = Math.max(this.viewportWidth, scaledWidth)
            const stageHeight = Math.max(this.viewportHeight, scaledHeight)
            return {
                scaledWidth,
                scaledHeight,
                stageWidth,
                stageHeight,
                offsetX: Math.max(0, (stageWidth - scaledWidth) / 2),
                offsetY: Math.max(0, (stageHeight - scaledHeight) / 2),
            }
        },
    },
    watch: {
        $route (to) {
            if (to.name === 'challenge' || to.name === 'challenges') this.navigate(to.params.id)
        },
        '$store.state.challenges': {
            handler () {
                if (!this.$store.state.modals.challengeModal.visible && this.$route.params.id) {
                    this.navigate(this.$route.params.id)
                }
            },
            deep: true,
        },
    },
    mounted () {
        this.navigate(this.$route.params.id)
        this.$nextTick(() => {
            this.updateViewportMetrics()
            this.centerMap()
        })
        window.addEventListener('resize', this.updateViewportMetrics)
        window.addEventListener('mouseup', this.dragEnd)
        window.addEventListener('blur', this.dragEnd)
    },
    beforeUnmount () {
        window.removeEventListener('resize', this.updateViewportMetrics)
        window.removeEventListener('mouseup', this.dragEnd)
        window.removeEventListener('blur', this.dragEnd)
        this.stopCameraAnimation()
    },
    methods: {
        stopCameraAnimation () {
            if (this.cameraAnimationFrame !== null) {
                cancelAnimationFrame(this.cameraAnimationFrame)
                this.cameraAnimationFrame = null
            }
        },
        updateViewportMetrics () {
            const viewport = this.$refs.viewport
            if (!viewport) return
            this.viewportWidth = viewport.clientWidth
            this.viewportHeight = viewport.clientHeight
        },
        getFrameForZoom (zoom) {
            const scaledWidth = MAP_WIDTH * zoom
            const scaledHeight = MAP_HEIGHT * zoom
            const stageWidth = Math.max(this.viewportWidth, scaledWidth)
            const stageHeight = Math.max(this.viewportHeight, scaledHeight)
            return {
                stageWidth,
                stageHeight,
                offsetX: Math.max(0, (stageWidth - scaledWidth) / 2),
                offsetY: Math.max(0, (stageHeight - scaledHeight) / 2),
            }
        },
        getCenteredScrollForZoom (zoom) {
            const frame = this.getFrameForZoom(zoom)
            return {
                left: Math.max(0, (frame.stageWidth - this.viewportWidth) / 2),
                top: Math.max(0, (frame.stageHeight - this.viewportHeight) / 2.6),
            }
        },
        animateCameraTo (targetZoom, targetLeft, targetTop, duration = 380) {
            const viewport = this.$refs.viewport
            if (!viewport) return

            this.stopCameraAnimation()
            this.updateViewportMetrics()

            const startZoom = this.zoom
            const startLeft = viewport.scrollLeft
            const startTop = viewport.scrollTop
            const startTime = performance.now()

            const step = (now) => {
                const progress = Math.min(1, (now - startTime) / duration)
                const eased = 1 - Math.pow(1 - progress, 3)

                this.zoom = Number((startZoom + (targetZoom - startZoom) * eased).toFixed(4))
                viewport.scrollLeft = startLeft + (targetLeft - startLeft) * eased
                viewport.scrollTop = startTop + (targetTop - startTop) * eased
                this.clampScroll()

                if (progress < 1) {
                    this.cameraAnimationFrame = requestAnimationFrame(step)
                    return
                }

                this.zoom = targetZoom
                viewport.scrollLeft = targetLeft
                viewport.scrollTop = targetTop
                this.clampScroll()
                this.cameraAnimationFrame = null
            }

            this.cameraAnimationFrame = requestAnimationFrame(step)
        },
        navigate (id) {
            if (id !== undefined && id in this.$store.state.challenges) {
                this.$store.commit('setModalChallenge', this.$store.state.challenges[id])
                this.$store.commit('showModal', 'challengeModal')
            } else {
                this.$store.commit('hideModal', 'challengeModal')
            }
        },
        centerMap () {
            const viewport = this.$refs.viewport
            if (!viewport) return
            this.stopCameraAnimation()
            this.updateViewportMetrics()
            const centered = this.getCenteredScrollForZoom(this.zoom)
            viewport.scrollLeft = centered.left
            viewport.scrollTop = centered.top
            this.clampScroll()
        },
        zoomIn () {
            this.setZoom(this.zoom + 0.15)
        },
        zoomOut () {
            this.setZoom(this.zoom - 0.15)
        },
        resetZoom () {
            this.selectedCategory = null
            this.updateViewportMetrics()
            const centered = this.getCenteredScrollForZoom(1)
            this.$nextTick(() => {
                this.animateCameraTo(1, centered.left, centered.top)
            })
        },
        setZoom (value, focus) {
            const viewport = this.$refs.viewport
            const nextZoom = Math.min(2.5, Math.max(0.5, Number(value.toFixed(2))))
            if (!viewport || nextZoom === this.zoom) {
                this.zoom = nextZoom
                return
            }

            this.updateViewportMetrics()
            const rect = viewport.getBoundingClientRect()
            const focusX = focus?.clientX ?? rect.left + viewport.clientWidth / 2
            const focusY = focus?.clientY ?? rect.top + viewport.clientHeight / 2
            const currentFrame = this.mapFrame
            const mapX = (viewport.scrollLeft + focusX - rect.left - currentFrame.offsetX) / this.zoom
            const mapY = (viewport.scrollTop + focusY - rect.top - currentFrame.offsetY) / this.zoom

            this.zoom = nextZoom
            this.$nextTick(() => {
                this.updateViewportMetrics()
                const nextFrame = this.mapFrame
                viewport.scrollLeft = mapX * this.zoom + nextFrame.offsetX - (focusX - rect.left)
                viewport.scrollTop = mapY * this.zoom + nextFrame.offsetY - (focusY - rect.top)
                this.clampScroll()
            })
        },
        zoomWheel (event) {
            this.setZoom(this.zoom + (event.deltaY < 0 ? 0.08 : -0.08), event)
        },
        dragStart (event) {
            if (event.button !== 0) return
            this.stopCameraAnimation()
            const viewport = this.$refs.viewport
            this.dragState = {
                x: event.clientX,
                y: event.clientY,
                left: viewport.scrollLeft,
                top: viewport.scrollTop,
                moved: false,
            }
        },
        dragMove (event) {
            if (!this.dragState) return
            const viewport = this.$refs.viewport
            const deltaX = event.clientX - this.dragState.x
            const deltaY = event.clientY - this.dragState.y
            if ((deltaX * deltaX + deltaY * deltaY) > (DRAG_OPEN_THRESHOLD * DRAG_OPEN_THRESHOLD)) {
                this.dragState.moved = true
            }
            viewport.scrollLeft = this.dragState.left - (event.clientX - this.dragState.x)
            viewport.scrollTop = this.dragState.top - (event.clientY - this.dragState.y)
            this.clampScroll()
        },
        dragEnd () {
            this.dragState = null
        },
        clampScroll () {
            const viewport = this.$refs.viewport
            if (!viewport) return

            this.updateViewportMetrics()
            const maxScrollLeft = Math.max(0, this.mapFrame.stageWidth - viewport.clientWidth)
            const maxScrollTop = Math.max(0, this.mapFrame.stageHeight - viewport.clientHeight)

            if (viewport.scrollLeft > maxScrollLeft) viewport.scrollLeft = maxScrollLeft
            if (viewport.scrollTop > maxScrollTop) viewport.scrollTop = maxScrollTop
            if (viewport.scrollLeft < 0) viewport.scrollLeft = 0
            if (viewport.scrollTop < 0) viewport.scrollTop = 0
        },
        focusCategory (category) {
            const viewport = this.$refs.viewport
            if (!viewport) return

            this.updateViewportMetrics()
            const targets = this.allMapTargets.filter(target => this.matchesFilter(target, category))
            if (!targets.length) {
                this.centerMap()
                return
            }

            const bounds = targets.reduce((acc, target) => {
                const left = Math.min(target.x - 12, target.cardLeft)
                const top = Math.min(target.y - 12, target.cardTop)
                const right = Math.max(target.x + 12, target.cardLeft + CARD_SIZE.width)
                const bottom = Math.max(target.y + 12, target.cardTop + CARD_SIZE.height)
                return {
                    minX: Math.min(acc.minX, left),
                    minY: Math.min(acc.minY, top),
                    maxX: Math.max(acc.maxX, right),
                    maxY: Math.max(acc.maxY, bottom),
                }
            }, {
                minX: Infinity,
                minY: Infinity,
                maxX: -Infinity,
                maxY: -Infinity,
            })

            const width = Math.max(1, bounds.maxX - bounds.minX + CATEGORY_FOCUS_PADDING_X * 2)
            const height = Math.max(1, bounds.maxY - bounds.minY + CATEGORY_FOCUS_PADDING_Y * 2)
            const nextZoom = Math.min(2.5, Math.max(0.5, Number(Math.min(
                viewport.clientWidth / width,
                viewport.clientHeight / height,
            ).toFixed(2))))

            const contentCenterX = (bounds.minX + bounds.maxX) / 2
            const contentCenterY = (bounds.minY + bounds.maxY) / 2
            const targetFrame = this.getFrameForZoom(nextZoom)
            const targetLeft = contentCenterX * nextZoom + targetFrame.offsetX - viewport.clientWidth / 2
            const targetTop = contentCenterY * nextZoom + targetFrame.offsetY - viewport.clientHeight / 2
            this.animateCameraTo(nextZoom, targetLeft, targetTop)
        },
        releaseChallenge (challenge, event) {
            const moved = this.dragState?.moved
            this.dragEnd()
            if (event && event.button !== 0) return
            if (moved) return
            if (challenge.status) return
            this.$router.push({ name: 'challenge', params: { id: challenge.id } })
        },
        toggleCategory (category) {
            const nextCategory = this.selectedCategory === category ? null : category
            this.selectedCategory = nextCategory
            this.$nextTick(() => {
                if (nextCategory) {
                    this.focusCategory(nextCategory)
                    return
                }
                this.resetZoom()
            })
        },
        markerClass (challenge) {
            const difficulty = this.challengeDifficulty(challenge)
            const state = this.markerState(challenge)
            return {
                [state]: true,
                'difficulty-medium': difficulty === 'medium',
                'difficulty-hard': difficulty === 'hard',
                'difficulty-extreme': difficulty === 'extreme',
            }
        },
        markerStyle (challenge) {
            const pulseOffset = (challenge.id * 137) % 1700
            const pulseDuration = 1.72 + ((challenge.id * 29) % 40) / 100
            const difficulty = this.challengeDifficulty(challenge)
            return {
                '--marker-color': `hsl(${190 + ((challenge.sectorIndex * 32) % 120)}deg 65% 46%)`,
                '--pulse-color': DIFFICULTY_PULSE_COLORS[difficulty],
                '--pulse-delay': `${-pulseOffset}ms`,
                '--pulse-duration': `${pulseDuration.toFixed(2)}s`,
                left: `${challenge.x}px`,
                top: `${challenge.y}px`,
            }
        },
        cardStyle (challenge) {
            return {
                left: `${challenge.cardLeft - challenge.x}px`,
                top: `${challenge.cardTop - challenge.y}px`,
            }
        },
        tooltip (challenge) {
            if (challenge.status === 1) return 'Locked route'
            if (challenge.status === 2) return 'Unpublished target'
            return 'Open location'
        },
        markerState (challenge) {
            if (this.isSolved(challenge)) return 'solved'
            if (challenge.status === 1) return 'locked'
            if (challenge.status === 2) return 'hidden'
            return 'live'
        },
        isSolved (challenge) {
            return this.$store.state.team && challenge.id in this.$store.state.team.solves
        },
        primaryCategory (challenge) {
            return challenge.categories?.[0] ?? ''
        },
        challengeDifficulty (challenge) {
            if (challenge.categories?.includes('extreme')) return 'extreme'
            if (challenge.categories?.includes('hard')) return 'hard'
            if (challenge.categories?.includes('medium')) return 'medium'
            return 'easy'
        },
        matchesFilter (challenge, filterName) {
            if (!filterName) return true
            if (filterName === 'easy') return challenge.categories?.includes('easy')
            if (filterName === 'medium') return challenge.categories?.includes('medium')
            if (filterName === 'hard') return challenge.categories?.includes('hard')
            if (filterName === 'extreme') return challenge.categories?.includes('extreme')
            if (filterName === 'onsite') return challenge.categories?.includes('onsite')
            return this.primaryCategory(challenge) === filterName
        },
        routeState (source, target) {
            if (this.isSolved(source) && this.isSolved(target)) return 'solved'
            if (target.status === 1) return 'locked'
            if (target.status === 2) return 'hidden'
            return 'live'
        },
        sectorStyle (sector) {
            const specialColors = {
                onsite: '#7a8b95',
                easy: '#22c55e',
                medium: '#eab308',
                hard: '#ef4444',
                extreme: '#000000',
            }
            const color = sector.type === 'special'
                ? specialColors[sector.name] || '#7a8b95'
                : `hsl(${190 + ((sector.index * 32) % 120)}deg 65% 46%)`
            return {
                '--sector-color': color,
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.briefing {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;

    p {
        max-width: 52rem;
        margin: 0.5rem 0 0;
    }
}

.briefing-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 0.35rem;
    min-width: 11rem;
    color: $color-muted-text;
}

.operations {
    display: grid;
    grid-template-columns: 16.5rem minmax(0, 1fr);
    gap: 1.25rem;
}

.legend-panel,
.map-viewport {
    background-color: $color-surface;
    border-radius: $standard-border-radius;
}

.legend-panel {
    padding: 1rem;
    height: fit-content;
}

.legend-copy {
    color: $color-muted-text;
}

.legend-list {
    display: grid;
    gap: 0.5rem;
    margin: 1rem 0;
}

.legend-list span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.swatch {
    display: inline-block;
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 50%;
}

.live {
    background-color: $challenge-color;
}

.solved {
    background-color: $color-success;
}

.sector-list {
    display: grid;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.sector-list-special {
    margin-top: 0.85rem;
}

.sector-row {
    appearance: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.5rem 0.65rem;
    border-top: 0;
    border-right: 0;
    border-bottom: 0;
    cursor: pointer;
    text-align: left;
    color: $color-text;
    border-left: 0.25rem solid var(--sector-color);
    background-color: rgba(255, 255, 255, 0.03);
    transition: background-color 120ms ease, transform 120ms ease;
}

.sector-row:hover {
    background-color: rgba(255, 255, 255, 0.06);
}

.sector-row.active {
    background-color: rgba(255, 255, 255, 0.11);
    transform: translateX(2px);
}

.zoom-controls {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
}

.map-viewport {
    position: relative;
    overflow: auto;
    height: calc(100vh - 12rem);
    min-height: 45rem;
    min-width: 0;
    cursor: grab;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: #063e4a;
}

.map-viewport:active {
    cursor: grabbing;
}

.map-stage {
    position: relative;
    background:
        radial-gradient(circle at 18% 18%, rgba(255, 255, 255, 0.04), transparent 20rem),
        linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent 25%),
        repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02) 1px, transparent 1px, transparent var(--grid-size, 60px)),
        repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02) 1px, transparent 1px, transparent var(--grid-size, 60px)),
        #063e4a;
}

.map-surface {
    position: absolute;
    left: 0;
    top: 0;
    width: 1520px;
    height: 1840px;
    transform-origin: top left;
    background: transparent;
}

.map-surface::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
        linear-gradient(90deg, transparent 65%, rgba(255, 255, 255, 0.03) 65.2%, transparent 65.4%),
        linear-gradient(transparent 0 8%, rgba(255, 255, 255, 0.03) 8.1%, transparent 8.2%);
    pointer-events: none;
}

.map-layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.sweden-outline {
    fill: rgba(246, 198, 107, 0.94);
    stroke: rgba(0, 0, 0, 0.15);
    stroke-width: 8;
    stroke-linejoin: round;
}

.watermark {
    position: absolute;
    color: rgba(255, 255, 255, 0.22);
    text-transform: uppercase;
    letter-spacing: 0.3em;
    font-size: 0.85rem;
}

.north {
    top: 40px;
    left: 40px;
}

.baltic {
    top: 940px;
    right: 120px;
    transform: rotate(90deg);
}

.route-line {
    stroke-width: 4;
    stroke-linecap: round;
    opacity: 0.95;
}

.route-line.live {
    stroke: $challenge-color;
}

.route-line.solved {
    stroke: $color-success;
}

.route-line.locked {
    stroke: #9a7a35;
    stroke-dasharray: 8 8;
}

.route-line.hidden {
    stroke: $color-neutral;
    stroke-dasharray: 4 10;
}

.target-marker {
    --marker-size: 11px;
    --marker-radius: 999px;
    --marker-shape: none;
    --pulse-size: 14px;
    position: absolute;
    width: 0;
    height: 0;
    border: 0;
    padding: 0;
    background: transparent;
    color: $color-text;
    text-align: left;
    cursor: pointer;
    overflow: visible;
}

.target-marker::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: var(--pulse-size);
    height: var(--pulse-size);
    border-radius: 999px;
    background-color: var(--pulse-color, rgba(134, 239, 172, 0.56));
    opacity: 0;
    pointer-events: none;
    z-index: 0;
    transform: translate(-50%, -50%);
    animation: target-pulse var(--pulse-duration, 1.9s) ease-out infinite;
    animation-delay: var(--pulse-delay, 0ms);
}

.target-pin {
    position: absolute;
    left: 50%;
    top: 50%;
    width: var(--marker-size);
    height: var(--marker-size);
    background-color: var(--marker-color);
    border-radius: var(--marker-radius);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.14);
    clip-path: var(--marker-shape);
    z-index: 1;
    transform: translate(-50%, -50%);
}

.target-marker.difficulty-medium {
    --marker-size: 14px;
    --marker-radius: 0;
    --marker-shape: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
    --pulse-size: 16px;
}

.target-marker.difficulty-medium::before {
    box-shadow:
        0 0 0 1px rgba(94, 63, 0, 0.28),
        0 0 12px rgba(234, 179, 8, 0.34);
}

.target-marker.live:not(.difficulty-medium):not(.difficulty-hard):not(.difficulty-extreme)::before {
    box-shadow:
        0 0 0 1px rgba(12, 84, 38, 0.3),
        0 0 12px rgba(34, 197, 94, 0.28);
}

.target-marker.difficulty-hard {
    --marker-size: 16px;
    --marker-radius: 0;
    --marker-shape: polygon(50% 0%, 61% 22%, 85% 15%, 78% 39%, 100% 50%, 78% 61%, 85% 85%, 61% 78%, 50% 100%, 39% 78%, 15% 85%, 22% 61%, 0% 50%, 22% 39%, 15% 15%, 39% 22%);
    --pulse-size: 16px;
}

.target-marker.difficulty-hard::before {
    box-shadow:
        0 0 0 1px rgba(111, 24, 24, 0.28),
        0 0 12px rgba(239, 68, 68, 0.3);
}

.target-marker.difficulty-extreme {
    --marker-size: 18px;
    --marker-radius: 0;
    --marker-color: #000000;
    --marker-shape: polygon(50% 0%, 58% 14%, 72% 2%, 74% 18%, 91% 9%, 82% 28%, 100% 31%, 87% 46%, 100% 59%, 82% 72%, 91% 91%, 74% 82%, 72% 98%, 58% 86%, 50% 100%, 42% 86%, 28% 98%, 26% 82%, 9% 91%, 18% 72%, 0% 59%, 13% 46%, 0% 31%, 18% 28%, 9% 9%, 26% 18%, 28% 2%, 42% 14%);
    --pulse-size: 20px;
}

.target-marker.difficulty-extreme::before {
    box-shadow:
        0 0 0 1px rgba(12, 12, 12, 0.7),
        0 0 16px rgba(0, 0, 0, 0.45);
}

.target-card {
    position: absolute;
    display: grid;
    width: 92px;
    gap: 0.1rem;
    padding: 0 0 0.42rem;
    text-align: left;
    text-decoration: none;
    overflow: visible;
    transform-origin: left top;
    transition: filter 140ms ease;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.58));
}

.target-tags {
    display: flex;
    align-items: center;
    gap: 0.12rem;
    min-height: 0.68rem;
    flex-wrap: wrap;
}

.target-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    text-transform: uppercase;
    white-space: nowrap;
    transition: background-color 140ms ease, box-shadow 140ms ease, color 140ms ease;
}

.target-tag-primary {
    min-height: 0.68rem;
    padding: 0 0.24rem 0 0.24rem;
    font-size: 0.27rem;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.98);
    background-color: var(--marker-color);
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.16),
        0 1px 3px rgba(0, 0, 0, 0.18);
}

.target-tag-secondary {
    min-height: 0.58rem;
    padding: 0 0.12rem;
    font-size: 0.24rem;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.74);
    background-color: color-mix(in srgb, var(--marker-color) 58%, rgba(112, 112, 112, 0.52));
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.target-title {
    display: block;
    margin: 0;
    font-size: 0.62rem;
    line-height: 1.12;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.94);
    transition: color 140ms ease;
}

.target-stats {
    display: flex;
    align-items: center;
    gap: 0.28rem;
    margin-top: 0.04rem;
    font-size: 0.46rem;
    color: rgba(255, 255, 255, 0.9);
    transition: color 140ms ease;
}

.target-stat {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
}

.target-stat + .target-stat::before {
    content: "\00b7";
    margin-right: 0.28rem;
    color: currentColor;
}

.target-stat-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.14rem;
    font-size: 0.48rem;
    line-height: 1;
    color: rgba(255, 255, 255, 0.98);
    transition: color 140ms ease;
}

.target-marker:hover .target-card {
    filter:
        drop-shadow(0 3px 8px rgba(0, 0, 0, 0.78))
        drop-shadow(0 0 10px rgba(255, 255, 255, 0.08))
        saturate(1.08);
}

.target-marker:hover .target-title,
.target-marker:hover .target-stats,
.target-marker:hover .target-stat-icon {
    color: rgba(255, 255, 255, 0.99);
}

.target-marker:hover .target-tag-primary {
    background-color: color-mix(in srgb, var(--marker-color) 86%, white 14%);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
}

.target-marker:hover .target-tag-secondary {
    color: rgba(255, 255, 255, 0.86);
    background-color: color-mix(in srgb, var(--marker-color) 78%, rgba(168, 168, 168, 0.52));
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.target-marker.solved .target-pin {
    background-color: $color-success;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.14);
}

.target-marker.solved .target-title,
.target-marker.solved .target-stats,
.target-marker.solved .target-tag,
.target-marker.solved .target-stat-icon {
    color: rgba(157, 255, 157, 0.95);
}

.target-marker.solved .target-tag-primary {
    color: rgba(255, 255, 255, 0.99);
    background-color: $color-success;
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.16),
        0 1px 3px rgba(0, 0, 0, 0.18);
}

.target-marker.solved .target-tag-secondary {
    color: rgba(255, 255, 255, 0.94);
    background-color: color-mix(in srgb, $color-success 36%, rgba(9, 20, 26, 0.4));
    box-shadow: inset 0 0 0 1px rgba(157, 255, 157, 0.14);
}

.target-marker.locked {
    cursor: default;
}

.target-marker.locked .target-pin {
    background-color: #9a7a35;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.18);
}

.target-marker.hidden {
    cursor: default;
}

.target-marker.hidden .target-pin {
    background-color: $color-neutral;
    box-shadow: none;
}

.target-marker.locked .target-title,
.target-marker.locked .target-stats,
.target-marker.locked .target-tag,
.target-marker.locked .target-stat-icon {
    color: rgba(215, 184, 117, 0.95);
}

.target-marker.hidden .target-title,
.target-marker.hidden .target-stats,
.target-marker.hidden .target-tag,
.target-marker.hidden .target-stat-icon {
    color: rgba(180, 180, 180, 0.85);
}

.empty-copy {
    margin-top: 1rem;
}

.target-marker.solved::before {
    animation: none;
    opacity: 0;
}

@keyframes target-pulse {
    0% {
        transform: translate(-50%, -50%) scale(0.88);
        opacity: 0.52;
    }

    70% {
        transform: translate(-50%, -50%) scale(1.8);
        opacity: 0;
    }

    100% {
        transform: translate(-50%, -50%) scale(1.8);
        opacity: 0;
    }
}

@media only screen and (max-width: 990px) {
    .operations {
        grid-template-columns: 1fr;
    }

    .briefing {
        flex-direction: column;
    }

    .briefing-meta {
        align-items: flex-start;
    }

    .map-viewport {
        height: 70vh;
        min-height: 34rem;
    }
}
</style>
